import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, ArrowRight, Radio } from "lucide-react";
import PulseAdvisoryModal from "./PulseAdvisoryModal";

/**
 * LATEST UPDATES modal
 * ────────────────────────────────────────────────────────────────
 * Fullscreen "Latest at TaxSetu AI" modal opened from the nav bar.
 * Keeps a curated, chronological list of newly-shipped features so
 * users can jump directly to what's new.
 *
 * To add a new entry: prepend to UPDATES array. Keep it short —
 * one bold title, one one-liner. Order = newest first.
 */

const UPDATES = [
  {
    label: "NEW",
    date: "08 Sep 2026",
    title: "Advance Tax Planner & Regime Optimizer",
    desc:
      "Complete FY 2026-27 planner: dual-regime comparison, auto Sec 403 (senior citizen) & Sec 404 (₹10k) exemptions, quarterly schedule per Sec 408, all mapped to Income-tax Act, 2025.",
    to: "/advance-tax",
    accent: "emerald",
  },
  {
    label: "NEW",
    date: "05 Sep 2026",
    title: "WhatsApp Consult — floating action button",
    desc:
      "Tap the WhatsApp bubble anywhere on the site to reach a TaxSetu expert instantly. 24×7 lead capture with pre-filled context.",
    to: null,
    accent: "green",
  },
  {
    label: "NEW",
    date: "02 Sep 2026",
    title: "Belated & Revised ITR countdown on Landing",
    desc:
      "Live countdown ticker for the 31 Dec 2026 belated-return deadline + 31 Mar 2027 revised-return window right on the hero card.",
    to: "/",
    accent: "amber",
  },
  {
    label: "UPDATED",
    date: "28 Aug 2026",
    title: "Live Compliance Extensions in AI Chat",
    desc:
      "MCA CCFS-2026 scheme, reserved-name resubmission window and all CBDT extensions injected into every relevant Chat answer with the notification ref.",
    to: "/chat",
    accent: "cyan",
  },
  {
    label: "NEW",
    date: "01 Jul 2026",
    title: "Statutory Format Library — 15 free templates for FY 2025-26",
    desc:
      "Auditor Report, Director Report, Registers, AGM Notice for Small Co, OPC & Section 8. Yellow-highlighted placeholders + Notification & Circular history at end of each doc.",
    to: "/formats",
    accent: "cyan",
  },
  {
    label: "NEW",
    date: "01 Jul 2026",
    title: "Companies Act 2013 now live in TaxSetu AI Chat",
    desc:
      "Ask AOC-4, MGT-7, board resolutions, AGM, Section 134, audit trail — anything Companies Act. Pick the 'Companies Act' domain pill in chat.",
    to: "/chat",
    accent: "cyan",
  },
  {
    label: "NEW",
    date: "28 Jun 2026",
    title: "Go Global — career pathways for Indian CAs",
    desc:
      "US / UK / Canada / Dubai roadmaps for finance professionals ready to serve clients across borders.",
    to: "/go-global",
    accent: "amber",
  },
  {
    label: "UPDATED",
    date: "20 Jun 2026",
    title: "Tax Calculator now supports FY 2025-26 & FY 2026-27",
    desc:
      "Dual financial year selector, 5 heads of income layout, capital gains 12.5%/20% split, HP loss cap, employer NPS, surcharge & cess breakdown.",
    to: "/calculator",
    accent: "cyan",
  },
  {
    label: "NEW",
    date: "15 Jun 2026",
    title: "Daily Newsletter — Stocks & Case Laws added",
    desc:
      "Notifications widget now covers Income Tax, GST, FEMA/RBI, Capital Markets and ITAT/SC Case Laws — refreshed every 2 hours.",
    to: "/",
    accent: "amber",
  },
];

const ACCENTS = {
  emerald: {
    label: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    ring: "hover:border-emerald-500/30 hover:shadow-emerald-500/10",
  },
  cyan: {
    label: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
    ring: "hover:border-cyan-500/30 hover:shadow-cyan-500/10",
  },
  amber: {
    label: "bg-amber-500/20 text-amber-300 border-amber-500/30",
    ring: "hover:border-amber-500/30 hover:shadow-amber-500/10",
  },
  green: {
    label: "bg-green-500/20 text-green-300 border-green-500/30",
    ring: "hover:border-green-500/30 hover:shadow-green-500/10",
  },
};

const CAT_ACCENT = {
  "Income Tax": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "GST": "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  "Labour Law": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Companies Act": "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
  "FEMA/RBI": "bg-sky-500/15 text-sky-300 border-sky-500/30",
  "Capital Markets": "bg-violet-500/15 text-violet-300 border-violet-500/30",
  "Case Laws": "bg-rose-500/15 text-rose-300 border-rose-500/30",
  "Stock Markets": "bg-lime-500/15 text-lime-300 border-lime-500/30",
  "Mixed": "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  const currentOrigin = typeof window !== "undefined" ? window.location.origin : "";
  if (
    currentOrigin &&
    !currentOrigin.includes("preview.emergentagent.com") &&
    !currentOrigin.includes("emergent.host")
  ) {
    return currentOrigin;
  }
  return envUrl;
};

export default function LatestUpdatesModal({ isOpen, onClose, onNavigate }) {
  const navigate = useNavigate();
  const [liveNews, setLiveNews] = useState([]);
  const [loadingNews, setLoadingNews] = useState(false);
  const [activePost, setActivePost] = useState(null);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Fetch live regulatory news when opened
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingNews(true);
    (async () => {
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/pulse/latest?limit=12`);
        const data = await res.json();
        if (!cancelled) setLiveNews((data && data.posts) || []);
      } catch {
        if (!cancelled) setLiveNews([]);
      } finally {
        if (!cancelled) setLoadingNews(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  return (
    <>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] overflow-y-auto"
          style={{ background: "#020617" }}
          data-testid="latest-updates-modal"
        >
          {/* Top bar */}
          <div className="sticky top-0 z-10 backdrop-blur-md bg-slate-950/80 border-b border-white/5">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="w-4 h-4 text-amber-300" />
                <span className="text-[11px] sm:text-xs font-bold tracking-widest text-amber-300 uppercase">
                  Latest Updates & Explore
                </span>
                <span className="hidden sm:inline text-[11px] font-medium tracking-wider text-slate-500 uppercase">
                  · Press ESC to close
                </span>
              </div>
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-white transition-colors p-1.5 rounded-md hover:bg-white/5"
                data-testid="latest-updates-close-btn"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 pb-16">
            {/* ── LIVE REGULATORY PULSE ── */}
            <div className="mb-10">
              <div className="flex items-center gap-2 mb-3">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500" />
                </span>
                <Radio className="w-4 h-4 text-rose-300" />
                <span className="text-[10.5px] font-bold tracking-widest text-rose-300 uppercase">
                  Regulatory Pulse · Live
                </span>
                <span className="text-[10.5px] font-medium tracking-widest text-slate-500 uppercase">
                  · Auto-refresh 2h
                </span>
              </div>
              <h2 className="text-xl sm:text-2xl font-heading font-bold text-white leading-tight mb-1">
                TaxSetu AI Blog — Regulatory Pulse
              </h2>
              <p className="text-[12.5px] sm:text-[13px] text-slate-400 mb-4">
                Original informational posts on the latest tax, GST, EPFO, MCA & RBI developments — written in-house by TaxSetu AI.
              </p>

              {loadingNews && liveNews.length === 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="animate-pulse rounded-xl border border-white/5 bg-white/[0.02] p-4 h-20" />
                  ))}
                </div>
              )}

              {!loadingNews && liveNews.length === 0 && (
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 text-[12.5px] text-slate-400">
                  Live feed is warming up — please check back in a couple of minutes.
                </div>
              )}

              {liveNews.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {liveNews.map((n, i) => {
                    const catCls = CAT_ACCENT[n.category] || CAT_ACCENT.Mixed;
                    const cleanTitle = (n.title || "")
                      .replace(/^TaxSetu AI Alert:\s*/i, "")
                      .replace(/^TaxSetu Insight:\s*/i, "");
                    return (
                      <button
                        key={`${n.slug}-${i}`}
                        onClick={() => setActivePost(n)}
                        data-testid={`regulatory-pulse-item-${i}`}
                        className="group text-left rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-rose-500/30 transition-all p-4"
                      >
                        <div className="flex items-start gap-3">
                          <span
                            className={`flex-shrink-0 inline-block text-[9px] font-bold uppercase tracking-widest border rounded-full px-2 py-0.5 ${catCls}`}
                          >
                            {n.category || "Mixed"}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-[13px] font-semibold text-white leading-snug line-clamp-2">
                                {cleanTitle}
                              </p>
                              <ArrowRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-rose-300 flex-shrink-0 mt-0.5" />
                            </div>
                            {n.teaser && (
                              <p className="mt-1 text-[11.5px] text-slate-400 line-clamp-2 leading-snug">
                                {n.teaser}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Section header */}
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 text-[10.5px] font-bold tracking-widest text-amber-300 uppercase mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                What&apos;s New
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight">
                Latest at TaxSetu AI
              </h2>
              <p className="mt-3 text-sm sm:text-[15px] text-slate-400 max-w-2xl">
                Recent updates and feature drops. Tap any update to jump straight to it.
              </p>
            </div>

            {/* Updates grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {UPDATES.map((u, i) => {
                const acc = ACCENTS[u.accent] || ACCENTS.cyan;
                const clickable = !!u.to;
                const [day, mon, year] = u.date.split(" ");
                return (
                  <button
                    key={i}
                    disabled={!clickable}
                    onClick={() => {
                      if (!clickable) return;
                      onClose();
                      onNavigate?.(u.to);
                    }}
                    data-testid={`latest-update-card-${i}`}
                    className={`text-left rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-all p-5 shadow-lg shadow-black/10 ${
                      clickable ? `cursor-pointer ${acc.ring}` : "cursor-default opacity-90"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Date column */}
                      <div className="flex-shrink-0 text-center min-w-[54px]">
                        <span
                          className={`inline-block text-[9.5px] font-bold uppercase tracking-widest border rounded-full px-2 py-0.5 mb-2 ${acc.label}`}
                        >
                          {u.label}
                        </span>
                        <div className="text-[11px] leading-tight text-slate-400 font-mono">
                          <div className="font-bold text-white text-[13px]">{day}</div>
                          <div className="uppercase">{mon}</div>
                          <div>{year}</div>
                        </div>
                      </div>
                      {/* Body */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="text-[15px] sm:text-base font-bold text-white leading-snug">
                            {u.title}
                          </h3>
                          {clickable && (
                            <ArrowRight className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                          )}
                        </div>
                        <p className="mt-1.5 text-[12.5px] text-slate-400 leading-relaxed">
                          {u.desc}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>

    <PulseAdvisoryModal
      post={activePost}
      isOpen={!!activePost}
      onClose={() => setActivePost(null)}
      onOpenPermalink={(slug) => {
        setActivePost(null);
        onClose();
        navigate(`/pulse/${slug}`);
      }}
    />
    </>
  );
}
